import { getApiErrorMessage } from "utils/apiErrorMessage";
import { AtraafSlotPayload } from "types/ziyarat";
import { AtraafSlot, AtraafSlotsSection } from "./AtraafSlotsSection";
import { LawazimPolicy, normalizeLawazimPolicy } from "types/ziyarat";
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { Label } from '../../../ui/label';
import { Badge } from '../../../ui/badge';
import { Checkbox } from '../../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '../../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import {
  Route,
  MapPin,
  Clock,
  Trash2,
  Plus,
  ArrowLeft,
  Sparkles,
  Car,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddAtraafPayload, AtraafStopResponse, CreateRouteStop, TransportType, Venue } from 'types/ziyarat';
import { FullScreenLoading } from 'components/ui/loader';
import { EditAtraaf, getAtraafByPublicId, getTravelTypes, getVenuesList, getZiyaratByVenueList } from 'services/ziyaratService';
import { useNavigate, useParams } from 'react-router-dom';
import { MultiSelect } from "ui/multi-select";
import { useScreenPermission } from 'hooks/useScreenPermission';
import { usePageHeader } from 'contexts/PageHeaderContext';


interface EditZiyaratRoutePageProps {
  onNavigate?: (page: string) => void;
}

export const EditZiyaratAtraafRoutePage = ({ onNavigate }: EditZiyaratRoutePageProps = {}) => {
  const navigate = useNavigate()
  const { setPageHeader } = usePageHeader();
  const { canEdit } = useScreenPermission();
  const params = useParams()
  const [loading, setLoading] = useState(false);
  const [transportTypesOptions, setTransportTypesOptions] = useState<TransportType[]>([]);
  const [venuesOptions, setVenuesOptions] = useState<Venue[]>([]);
  const [ziyaratVenuesOptions, setZiyaratVenuesOptions] = useState<Venue[]>([]);
  const [errors, setErrors] = useState({
    venue: "",
    routeName: "",
    cap: "",
    stops: ""
  });


  useEffect(() => {
    setPageHeader({
      title: "Edit Ziyarat Atraaf",
      childTitle: "Edit ziyarat atraaf with multiple stops",
      icon: <Route className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);



  const loadEditData = useCallback(async (publicId: string) => {
    try {
      setLoading(true);

      const data = await getAtraafByPublicId(publicId);

      setFormData((prev) => ({
        ...prev,
        venue: data.venueId?.toString() ?? "",
        routeName: data.atraafName ?? "",
        cap: String(data.capacity ?? ""),
        minDays: String(data.minDays ?? ""),
        sortPriority: String(data.sortPriority ?? ""),
        weekdays: data.availableDays
          ? data.availableDays.split(",")
          : [],
         travelTime: data.durationHours?.slice(0, 5) ?? "",
        isTransportRequired: data.transportRequired ?? true,
        isOptional: data.optionalRoute ?? false,
        lawazimPolicy: normalizeLawazimPolicy(data.lawazimPolicy ?? data.lawazimRequired),
        isActive: data.isActive ?? true,

        slots: (data.slots ?? []).map((slot: AtraafSlotPayload, index: number) => ({
          id: `saved-slot-${slot.slotId ?? index}`,
          slotId: slot.slotId,
          name: slot.slotName,
          startTime: slot.startTime?.slice(0, 5) ?? "",
          capacity: slot.capacity,
          isActive: slot.isActive,
        })),
        stops: data.details?.map((stop: AtraafStopResponse, index: number) => ({
          id: String(index + 1),
          // placeName: stop.ziyaratName,
          placeName: stop.ziyaratId,
          selectedPlace: stop.ziyaratId,
          travelTime: stop.travelTimeMinutes?.slice(0, 5),
          duration: stop.durationHours?.slice(0, 5),
          transportType: stop.transportType,
          isOptional: stop.optionalFlag
        })) ?? []
      }));
    } catch {
      toast.error("Failed to load route details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (params.id) {
      loadEditData(params.id);
    }
  }, [params.id, loadEditData]);


  const [formData, setFormData] = useState({
    venue: '',
    routeName: '',
    cap: '',
    travelTime: '',
    isTransportRequired: true,
    isOptional: false,
    isActive: true,
    lawazimPolicy: 0 as LawazimPolicy,
    selectedPlace: '',
    stopTravelTime: '',
    stopDuration: '',
    stopTransportType: '',
    isStopOptional: false,

    stops: [] as CreateRouteStop[],
    slots: [] as AtraafSlot[],

    minDays: '',
    sortPriority: '',
    weekdays: [] as string[],
  });



  // API CALLING START
  const getTransportTypeListApi = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTravelTypes();
      setTransportTypesOptions(data);
    } catch {
      toast.error("Failed to transport types");
    } finally {
      setLoading(false);
    }
  }, []);

  const getVenuesListApi = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVenuesList({ onlyActive: true });
      setVenuesOptions(data);
    } catch {
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  }, []);


  const getZiyaratByVenuesListApi = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getZiyaratByVenueList(formData.venue);
      setZiyaratVenuesOptions(data)
    } catch {
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  }, [formData.venue]);

  useEffect(() => {
    getVenuesListApi();
    getTransportTypeListApi()
  }, [getVenuesListApi, getTransportTypeListApi]);

  useEffect(() => {
    if (!formData.venue) return;
    getZiyaratByVenuesListApi();
  }, [formData.venue, getZiyaratByVenuesListApi]);

  // const getAvailablePlaces = () => {
  //   if (!formData.venue) return [];
  //   return placesByVenue[formData.venue] || [];
  // };

  // Calculate total duration from all stops
  const calculateTotalDuration = () => {
    if (formData.stops.length === 0) return '00:00';

    // Calculate based on start time plus duration for each stop
    let earliestStartMinutes = Infinity;
    let latestEndMinutes = 0;

    formData.stops.forEach(stop => {
      if (stop.travelTime && stop.duration) {
        // Parse travel time (start time)
        const [startHours, startMinutes] = stop.travelTime.split(':').map(Number);
        const startTotalMinutes = (startHours * 60) + startMinutes;

        // Parse duration
        const [durationHours, durationMinutes] = stop.duration.split(':').map(Number);
        const durationTotalMinutes = (durationHours * 60) + durationMinutes;

        // Calculate end time for this stop
        const endTotalMinutes = startTotalMinutes + durationTotalMinutes;

        // Track earliest start and latest end
        if (startTotalMinutes < earliestStartMinutes) {
          earliestStartMinutes = startTotalMinutes;
        }
        if (endTotalMinutes > latestEndMinutes) {
          latestEndMinutes = endTotalMinutes;
        }
      }
    });

    // Calculate total duration from earliest start to latest end
    if (earliestStartMinutes === Infinity || latestEndMinutes === 0) {
      return '00:00';
    }

    const totalMinutes = latestEndMinutes - earliestStartMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const handleEditStop = () => {
    if (!formData.venue) {
      toast.error('Please select a venue first');
      return;
    }
    if (!formData.selectedPlace) {
      toast.error('Please select a place');
      return;
    }
    if (!formData.stopTravelTime) {
      toast.error('Please enter travel time for this stop');
      return;
    }
    if (!formData.stopDuration) {
      toast.error('Please enter duration of stay for this stop');
      return;
    }
    if (!formData.stopTransportType) {
      toast.error('Please select transport type for this stop');
      return;
    }

    const selectedziyarat = ziyaratVenuesOptions.find(
      (item) => String(item.ziyaratId) === formData.selectedPlace
    )

    const newStop: CreateRouteStop = {
      id: Date.now().toString(),
      placeName: Number(selectedziyarat?.ziyaratId) ?? "",
      travelTime: formData.stopTravelTime,
      duration: formData.stopDuration,
      transportType: formData.stopTransportType,
      isOptional: formData.isStopOptional
    };

    setFormData({
      ...formData,
      stops: [...formData.stops, newStop],
      selectedPlace: '',
      stopTravelTime: '',
      stopDuration: '',
      stopTransportType: '',
      isStopOptional: false
    });
    toast.success('Stop added to route');
  };

  const handleRemoveStop = (stopId: string) => {
    setFormData({
      ...formData,
      stops: formData.stops.filter(stop => stop.id !== stopId)
    });
    toast.success('Stop removed');
  };


  const validateAddForm = () => {
    const newErrors = {
      venue: "",
      routeName: "",
      cap: "",
      stops: ""
    };

    let isValid = true;

    if (!formData.venue.trim()) {
      newErrors.venue = "Please select venue";
      isValid = false;
    }

    if (!formData.routeName.trim()) {
      newErrors.routeName = "Please enter atraaf name";
      isValid = false;
    }

    if (!formData.cap.trim()) {
      newErrors.cap = "Please enter capacity";
      isValid = false;
    }

    if (formData.stops.length === 0) {
      // newErrors.stops = "Please add at least one stop";
      toast.error('Please add at least one stop');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };



  const handleUpdateZiyarat = async () => {
    if (!validateAddForm()) return;

    try {
      const payload: AddAtraafPayload = {
        publicId: String(params.id),
        venueId: Number(formData.venue),
        atraafName: formData.routeName,
        capacity: Number(formData.cap || 0),
        minDays: Number(formData.minDays || 0),
        sortPriority: Number(formData.sortPriority || 0),
        availableDays: formData.weekdays.join(","),
        durationHours: calculateTotalDuration(),
        transportRequired: formData.isTransportRequired,
        optionalRoute: formData.isOptional,
        lawazimPolicy: formData.lawazimPolicy,
        isActive: formData.isActive,
        slots: formData.slots.map((slot) => ({
          slotName: slot.name,
          startTime: slot.startTime,
          capacity: slot.capacity,
          isActive: slot.isActive,
          slotId: slot.slotId ?? 0,
        })),
        details: formData.stops.map((stop) => ({
          ziyaratId: Number(stop.placeName ?? 0),
          travelTimeMinutes: stop.travelTime ?? 0,
          durationHours: stop.duration ?? 0,
          transportType: String(stop.transportType ?? ""),
          optionalFlag: Boolean(stop.isOptional)
        }))
      };

      console.log("Atraaf values before submission", {
        lawazimPolicy: formData.lawazimPolicy,
        slots: payload.slots,
      });
      await EditAtraaf(payload)
      toast.success("Ziyarat atraaf created successfully!");
      navigate('/atraaf')
    } catch (error) {
      console.error(error);
      toast.error(getApiErrorMessage(error, "Failed to update atraaf"));
    }
  };



  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate(-1) }}
            className="hover:bg-gray-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </div>
        <div className="flex flex-wrap justify-end gap-2 self-end">
          <Button
            variant="outline"
            onClick={() => { navigate(-1) }}
            className="border-gray-300"
          >
            Cancel
          </Button>
          {canEdit && (
            <Button
              onClick={handleUpdateZiyarat}
              style={{ background: '#5B9BD5' }}
              className="text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Update Atraaf
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {/* Basic Information Card */}
        <Card className="border-gray-200 shadow-sm bg-white">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-5 bg-gradient-to-b from-[#5B9BD5] to-[#4A7FB0] rounded-full"></div>
              <h2 className="text-base font-semibold text-gray-900">Basic Information</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
<div className="min-w-0 space-y-1.5">
                <SelectFieldset label="Venue" required>
                  <Select
                    value={formData.venue}
                    onValueChange={(value) => {
                      setFormData({ ...formData, venue: value, stops: [], slots: [], selectedPlace: '' })
                      setErrors({ ...errors, venue: "" });
                    }}
                  >
                    <SelectTriggerUI className="text-sm">
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {venuesOptions.map((venue) => (
                        <SelectItem key={venue.venueId} value={venue.venueId.toString()}>
                          {venue.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {/* {formData.venue && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {getAvailablePlaces().length} places available in {formData.venue}
                  </p>
                )} */}
                {errors.venue && (
                  <p className="text-red-500 text-xs mt-1">{errors.venue}</p>
                )}
              </div>
<div className="min-w-0 space-y-1.5">
                <div className="relative">
                  {/* <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                  <InputFieldset
                    label="Duration (in hrs) (Auto-calculated)"
                    id="duration"
                    type="text"
                    value={calculateTotalDuration()}
                    readOnly
                    className="text-sm bg-gray-50 cursor-not-allowed"
                    placeholder="00:00"
                  />
                </div>
                {formData.stops.length > 0 && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Total from {formData.stops.length} stop{formData.stops.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
<div className="min-w-0 space-y-1.5">
                <InputFieldset
                  label="Atraaf Name"
                  required
                  id="routeName"
                  value={formData.routeName}
                  onChange={(e) => {
                    setFormData({ ...formData, routeName: e.target.value });
                    setErrors({ ...errors, routeName: "" });
                  }}
                  placeholder="e.g., BAGHDAD ATRAAF ATBAATAR AALIYAH"
                  className="text-sm"
                />
                {errors.routeName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.routeName}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
<div className="min-w-0 space-y-1.5">
                <InputFieldset
                  type="number"
                  label="Cap"
                  required
                  id="cap"
                  value={formData.cap}
                  onChange={(e) => {
                    setFormData({ ...formData, cap: e.target.value });
                    setErrors({ ...errors, cap: "" });
                  }}
                  placeholder="00"
                  className="text-sm"
                />
                {errors.cap && (
                  <p className="text-red-500 text-xs mt-1">{errors.cap}</p>
                )}
              </div>
<div className="min-w-0 space-y-1.5">
                  <div className="relative">
                    <InputFieldset
                      label="Min Days"
                      id="minDays"
                      type="number"
                      value={formData.minDays}
                      onChange={(e) => {
                        setFormData({ ...formData, minDays: e.target.value });
                        // setErrors({ ...errors, minDays: "" });
                      }}
                      className="text-sm bg-gray-50"
                      placeholder="00"
                    />
                  </div>
                  {/* {formData.stops.length > 0 && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" />
                                  Total from {formData.stops.length} stop{formData.stops.length !== 1 ? 's' : ''}
                                </p>
                              )} */}
                </div>
<div className="min-w-0 space-y-1.5">
                  <div className="relative">
                    <InputFieldset
                      label="Sort Priority"
                      id="minDays"
                      type="number"
                      value={formData.sortPriority}
                      onChange={(e) => {
                        setFormData({ ...formData, sortPriority: e.target.value });
                        // setErrors({ ...errors, minDays: "" });
                      }}
                      className="text-sm bg-gray-50"
                      placeholder="0"
                    />
                  </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
<div className="min-w-0 space-y-1.5">
                <SelectFieldset label="Lawazim Policy">
                  <Select
                    value={String(formData.lawazimPolicy)}
                    onValueChange={(value) =>
                      setFormData({ ...formData, lawazimPolicy: normalizeLawazimPolicy(value) })
                    }
                  >
                    <SelectTriggerUI aria-label="Lawazim Policy" className="text-sm bg-white border-gray-300 rounded-lg">
                      <SelectValue placeholder="Type" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="0">No (Not required)</SelectItem>
                      <SelectItem value="1">Always (Required - Always)</SelectItem>
                      <SelectItem value="2">First Time Free (Required - First Time Free)</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
<div className="min-w-0 space-y-1.5">
                <Label htmlFor="minDays" className="text-sm font-medium text-gray-700">
                  Available Days
                  {/* <span className="text-xs text-gray-500"></span> */}
                </Label>
                <div className="relative">
                  <MultiSelect
                    options={[
                      { value: 'Sunday', label: 'Sunday' },
                      { value: 'Monday', label: 'Monday' },
                      { value: 'Tuesday', label: 'Tuesday' },
                      { value: 'Wednesday', label: 'Wednesday' },
                      { value: 'Thursday', label: 'Thursday' },
                      { value: 'Friday', label: 'Friday' },
                      { value: 'Saturday', label: 'Saturday' },
                    ]}
                    selected={formData.weekdays}
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        weekdays: selected,
                      }))
                    }
                    placeholder="Select Weekdays..."
                  />
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-3 gap-3 pt-1">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
                <Checkbox
                  id="isTransportRequired"
                  checked={formData.isTransportRequired}
                  onCheckedChange={(checked) => setFormData({ ...formData, isTransportRequired: checked as boolean })}
                  className="data-[state=checked]:bg-[#5B9BD5] data-[state=checked]:border-[#5B9BD5]"
                />
                <Label htmlFor="isTransportRequired" className="text-xs font-medium cursor-pointer text-gray-700">
                  Transport Required?
                </Label>
              </div>

              <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100">
                <Checkbox
                  id="isOptional"
                  checked={formData.isOptional}
                  onCheckedChange={(checked) => setFormData({ ...formData, isOptional: checked as boolean })}
                  className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                />
                <Label htmlFor="isOptional" className="text-xs font-medium cursor-pointer text-gray-700">
                  Optional Route?
                </Label>
              </div>



              <div className="flex items-center gap-2 p-2 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked as boolean })}
                  className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                />
                <Label htmlFor="isActive" className="text-xs font-medium cursor-pointer text-gray-700">
                  Active Status?
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <AtraafSlotsSection
          key={formData.venue}
          slots={formData.slots}
          onChange={(slots) => setFormData((previous) => ({ ...previous, slots }))}
        />

        {/* Atraaf Details Card */}
        <Card className="border-gray-200 shadow-sm bg-white">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-5 bg-gradient-to-b from-[#5B9BD5] to-[#4A7FB0] rounded-full"></div>
              <h2 className="text-base font-semibold text-gray-900">Atraaf Details</h2>
              {/* {errors.stops && (
                <p className="text-red-500 text-xs mt-1">{errors.stops}</p>
              )} */}
            </div>

            {/* Add Stop Section */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-3 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-3 space-y-1.5">
                  <SelectFieldset label="Select Place">
                    <Select
                      value={formData.selectedPlace}
                      onValueChange={(value) => setFormData({ ...formData, selectedPlace: value })}
                      disabled={!formData.venue}
                    >
                      <SelectTriggerUI className="text-sm bg-white border-gray-300 rounded-lg">
                        <SelectValue placeholder="Select a place" />
                      </SelectTriggerUI>
                      {/* <SelectContent className="bg-white rounded-lg max-h-72"> */}
                      {/* {getAvailablePlaces().map(place => (
                        <SelectItem key={place} value={place} className="text-sm py-2 cursor-pointer hover:bg-gray-50">
                          {place}
                        </SelectItem>
                      ))} */}
                      {/* </SelectContent> */}
                      <SelectContent className="bg-white rounded-lg max-h-72">
                        {ziyaratVenuesOptions.map((place) => (
                          <SelectItem key={place.ziyaratId} value={place.ziyaratId.toString()}>
                            {place.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>

                {/* <div className="md:col-span-2 space-y-1.5">
                  <InputFieldset
                    label="Travel Time"
                    type="time"
                    value={formData.stopTravelTime}
                    onChange={(e) => setFormData({ ...formData, stopTravelTime: e.target.value })}
                    className="text-sm bg-white border-gray-300 rounded-lg"
                  />
                </div> */}

                <div className="md:col-span-2 space-y-1.5">
                  <InputFieldset
                    label="Travel Time"
                    type="text"
                    value={formData.stopTravelTime}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d:]/g, "");
                      if (value.length === 2 && !value.includes(":")) {
                        value += ":";
                      }
                      value = value.slice(0, 5);
                      setFormData({
                        ...formData,
                        stopTravelTime: value,
                      });
                    }}
                    placeholder="HH:MM"
                    pattern="^([01]\\d|2[0-3]):([0-5]\\d)$"
                    maxLength={5}
                    className="text-sm bg-white border-gray-300 rounded-lg"
                  />
                </div>

                {/* <div className="md:col-span-2 space-y-1.5">
                  <InputFieldset
                    label="Duration"
                    type="time"
                    value={formData.stopDuration}
                    onChange={(e) => setFormData({ ...formData, stopDuration: e.target.value })}
                    className="text-sm bg-white border-gray-300 rounded-lg"
                  />
                </div> */}

                <div className="md:col-span-2 space-y-1.5">
                  <InputFieldset
                    label="Duration"
                    type="text"
                    value={formData.stopDuration}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d:]/g, "");

                      // Auto-add colon after HH
                      if (value.length === 2 && !value.includes(":")) {
                        value += ":";
                      }

                      // Limit to HH:MM
                      value = value.slice(0, 5);

                      setFormData({
                        ...formData,
                        stopDuration: value,
                      });
                    }}
                    placeholder="HH:MM"
                    pattern="^([01]\\d|2[0-3]):([0-5]\\d)$"
                    maxLength={5}
                    className="text-sm bg-white border-gray-300 rounded-lg"
                  />
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-sm font-medium text-gray-900"></Label>
                  <SelectFieldset label="Transport Type">
                    <Select
                      value={formData.stopTransportType}
                      onValueChange={(value) => setFormData({ ...formData, stopTransportType: value })}
                    >
                      <SelectTriggerUI className="text-sm bg-white border-gray-300 rounded-lg">
                        <SelectValue placeholder="Type" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {transportTypesOptions.map((item) => (
                          <SelectItem key={item.publicId} value={item.travelTypeName}>
                            {item.travelTypeName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>

                <div className="md:col-span-1 flex items-center justify-center h-9">
                  <div className="flex items-center gap-1.5">
                    <Checkbox
                      id="stopOptional"
                      checked={formData.isStopOptional}
                      onCheckedChange={(checked) => setFormData({ ...formData, isStopOptional: checked as boolean })}
                      className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    />
                    <Label htmlFor="stopOptional" className="text-xs font-medium cursor-pointer text-gray-700">
                      Optional
                    </Label>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Button
                    type="button"
                    onClick={handleEditStop}
                    style={{ background: '#5B9BD5' }}
                    className="text-white w-full h-9 rounded-lg shadow-sm hover:shadow-md transition-shadow text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Stops Table */}
            {formData.stops.length > 0 ? (
              <div className="border border-[#5B9BD5]/30 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow style={{ background: '#5B9BD5' }} className="hover:bg-[#5B9BD5]">
                      <TableHead className="text-white text-xs font-semibold py-2">S.No.</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2">Place Name</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2">Travel Time</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2">Duration</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2">Transport Type</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2">Status</TableHead>
                      <TableHead className="text-white text-xs font-semibold py-2 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.stops.map((stop, index) => (
                      <TableRow key={stop.id} className="hover:bg-[#5B9BD5]/5 transition-colors border-b border-[#5B9BD5]/10">
                        <TableCell className="text-sm font-medium py-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#5B9BD5] to-[#4A7FB0] flex items-center justify-center text-white text-xs font-bold">
                            {index + 1}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm py-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-[#5B9BD5]" />
                            {/* <span className="font-medium text-gray-900">{stop.placeName}</span> */}
                            <span className="font-medium text-gray-900">
                              {ziyaratVenuesOptions.find((item) => String(item.ziyaratId) === String(stop.placeName))?.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm py-2">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-[#5B9BD5]" />
                            <span className="text-gray-700">{stop.travelTime}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm py-2">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-[#5B9BD5]" />
                            <span className="text-gray-700">{stop.duration}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm py-2">
                          <div className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-[#5B9BD5]" />
                            <span className="text-gray-700">{stop.transportType}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm py-2">
                          {stop.isOptional ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs py-0.5">
                              Optional
                            </Badge>
                          ) : (
                            <Badge style={{ background: '#5B9BD5' }} className="text-white text-xs py-0.5">
                              Mandatory
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm py-2 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveStop(stop.id)}
                            className="text-red-500 hover:bg-red-50 h-7 w-7 p-0 rounded-lg"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center px-4 py-3 bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg border-2 border-dashed border-gray-300">
                <MapPin className="w-6 h-6 mx-auto mb-1 text-gray-300" />
                <p className="text-sm text-gray-500 font-medium">No stops added yet</p>
                <p className="text-xs text-gray-400 mt-1">Add stops to create your ziyarat atraaf</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
